import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console and potentially to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // TODO: Log to error reporting service (e.g., Sentry, LogRocket, etc.)
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="container mt-5">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="card shadow border-danger">
                <div className="card-body p-4 text-center">
                  <div className="mb-4">
                    <i className="fas fa-exclamation-triangle text-danger fa-4x mb-3"></i>
                    <h2 className="text-danger">Oops! Something went wrong</h2>
                    <p className="lead text-muted">
                      We're sorry, but something unexpected happened. Don't worry, it's not your fault!
                    </p>
                  </div>

                  <div className="alert alert-light border" role="alert">
                    <h6 className="alert-heading">What happened?</h6>
                    <p className="mb-0">
                      A technical error occurred while loading this page. Our team has been notified and is working to fix it.
                    </p>
                  </div>

                  <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-4">
                    <button 
                      className="btn btn-primary"
                      onClick={this.handleReload}
                    >
                      <i className="fas fa-redo me-2"></i>
                      Try Again
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={this.handleGoHome}
                    >
                      <i className="fas fa-home me-2"></i>
                      Go Home
                    </button>
                  </div>

                  {/* Show error details in development */}
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <details className="text-start">
                      <summary className="btn btn-outline-info btn-sm mb-3">
                        Show Technical Details (Development Only)
                      </summary>
                      <div className="alert alert-warning">
                        <h6>Error Details:</h6>
                        <pre className="small text-wrap">
                          {this.state.error.toString()}
                        </pre>
                        {this.state.errorInfo && (
                          <>
                            <h6 className="mt-3">Component Stack:</h6>
                            <pre className="small text-wrap">
                              {this.state.errorInfo.componentStack}
                            </pre>
                          </>
                        )}
                      </div>
                    </details>
                  )}

                  <div className="mt-4 p-3 bg-light rounded">
                    <small className="text-muted">
                      <strong>Still having issues?</strong><br />
                      Contact our support team at <a href="mailto:support@eazyhire.co.za">support@eazyhire.co.za</a>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;